import { useMemo } from 'react'

import type { Filter } from '@cubejs-client/core'
import {
  CONVERSATION_FACTS_ZONE_DIMENSIONS,
  DRIFT_CHAT_USERS_AVAILABILITY_BUCKETS,
  resultSetToTableData,
} from 'cube'
import {
  RespondedChatsQuery,
  TeammateAvailabilityBaseQuery,
} from 'cube/queries'
import moment from 'moment'

import {
  CubeQuery,
  CubeQueryBuilder,
  FlagIndicatorTypeEnum,
  GanttViewTypeEnum,
  IAvailabilityDataElement,
  IFlagIndicator,
  useCubeQueries,
} from '@driftt/dds.reports'

import {
  TeammateAvailabilityRespondedChats,
  TeammateAvailabilityRow,
} from './teammateAvailability.types'

export interface TeammateAvailabilityResult {
  tableData: TeammateAvailabilityRow[]
  isLoadingData: boolean
  isErrorFetchingData: boolean
}
const sortByHighestAvailability = (
  a: IAvailabilityDataElement,
  b: IAvailabilityDataElement
) => {
  const availabilityA = a.availability,
    availabilityB = b.availability
  // Compare the 2 available times
  return availabilityA < availabilityB
    ? 1
    : availabilityA > availabilityB
    ? -1
    : 0
}

/***
 * Hook that uses cube to fetch Teammate Availability data
 *
 * @returns {TeammateAvailabilityResult}
 */
export const useTeammateAvailabilityBuckets = (
  currentViewStartDate: Date | string | number,
  ganttViewFormat: GanttViewTypeEnum,
  teamFilters: Filter[],
  startDate: Date | string | number,
  endDate: Date | string | number
) => {
  const dateFormat = 'YYYY-MM-DD'
  const queries: CubeQuery[] = []

  const availabilityBucketsCubeQuery: CubeQuery = new CubeQueryBuilder({
    query: TeammateAvailabilityBaseQuery,
  })
    .withTimeDimensions([
      {
        dimension:
          DRIFT_CHAT_USERS_AVAILABILITY_BUCKETS.AVAILABILITY_BUCKET_START_AT,
        dateRange: [
          moment(startDate).format(dateFormat),
          moment(endDate).format(dateFormat),
        ],
      },
    ])
    .withFilters([...teamFilters])
    .build()

  queries.push(availabilityBucketsCubeQuery)

  const repondedChatsCubeQuery: CubeQuery = new CubeQueryBuilder({
    query: RespondedChatsQuery,
  })
    .withTimeDimensions([
      {
        dimension: CONVERSATION_FACTS_ZONE_DIMENSIONS.CONVERSATION_CREATED_AT,
        dateRange: [
          moment(startDate).format(dateFormat),
          moment(endDate).format(dateFormat),
        ],
      },
    ])
    .withFilters([...teamFilters])
    .build()

  if (ganttViewFormat === GanttViewTypeEnum.DAY) {
    // NOTE: Should only run the repondedChatsCubeQuery query in the DAY view
    queries.push(repondedChatsCubeQuery)
  }

  const queriesResult = useCubeQueries({
    queries: queries,
  })

  const isLoadingData = queriesResult.some((result) => result.isLoading)
  const isErrorFetchingData = queriesResult.some((result) => result.isError)

  const reduceRespondedChats = (
    accumulator: { [x: string]: IFlagIndicator[] },
    currentItem: TeammateAvailabilityRespondedChats
  ) => {
    const {
      userId,
      conversationId,
      conversationCreatedAtWithZone,
      hasRoutedAndSomeoneRespondedWithin60s,
    } = currentItem

    if (accumulator[userId]) {
      accumulator[userId].push({
        id: conversationId,
        start: conversationCreatedAtWithZone,
        type: hasRoutedAndSomeoneRespondedWithin60s
          ? FlagIndicatorTypeEnum.SUCCESS
          : FlagIndicatorTypeEnum.ERROR,
      } as IFlagIndicator)
    } else {
      accumulator[userId] = [
        {
          id: conversationId,
          start: currentItem?.conversationCreatedAtWithZone,
          type: currentItem?.hasRoutedAndSomeoneRespondedWithin60s
            ? FlagIndicatorTypeEnum.SUCCESS
            : FlagIndicatorTypeEnum.ERROR,
        } as IFlagIndicator,
      ]
    }
    return accumulator
  }

  const tableData = useMemo(() => {
    if (!isLoadingData) {
      const teammateAvailabilityBucketsData = resultSetToTableData(
        queriesResult[0].data
      ) as TeammateAvailabilityRow[]

      let repondedChatsData: TeammateAvailabilityRespondedChats[]
      let reducedRespondedChats = {}

      if (ganttViewFormat === GanttViewTypeEnum.DAY) {
        repondedChatsData = resultSetToTableData(
          queriesResult[1].data
        ) as TeammateAvailabilityRespondedChats[]

        reducedRespondedChats = repondedChatsData.reduce(
          reduceRespondedChats,
          {}
        )
      }

      const reducedTeammateAvailabilityRows =
        teammateAvailabilityBucketsData.reduce(
          (accumulator, currentItem, index) => {
            const {
              availabilityStartAtWithZone,
              availabilityEndAtWithZone,
              userId,
              bucketSum,
              displayName,
            } = currentItem

            if (accumulator[userId]) {
              // When the element is already added into the accumulator array, it accumulates (sum) the amount of availability minutes of each bucket
              accumulator[userId].availability += parseInt(bucketSum)
              // It also adds the task(s) into the object with the same id inside the accumulator
              accumulator[userId].tasks.push({
                start: availabilityStartAtWithZone,
                end: availabilityEndAtWithZone,
                row: index,
              })
            } else {
              // When there is no object that coincides in the accumulator, it means we have not added that teammate yet, we need to add it completely.
              // We already have all the flag indicators per teammate in reducedRespondedChats, which is why it includes them the first time.

              const flagIndicators = reducedRespondedChats[userId] // look for the flags for the current userId and put it in the data object

              accumulator[userId] = {
                id: userId,
                name: displayName,
                availability: parseInt(bucketSum ?? 0),
                flagIndicators: flagIndicators ? flagIndicators : [],
                tasks: [
                  {
                    start: availabilityStartAtWithZone,
                    end: availabilityEndAtWithZone,
                    row: index,
                  },
                ],
              } as IAvailabilityDataElement
            }

            return accumulator
          },
          {}
        )

      const availabilityDataElement = Object.values(
        reducedTeammateAvailabilityRows
      ) as IAvailabilityDataElement[]

      // Sort by results by the teammates with highest availability
      return availabilityDataElement.sort(sortByHighestAvailability)
    }
    return []
  }, [ganttViewFormat, isLoadingData, queriesResult])

  return useMemo(
    () => ({ tableData, isLoadingData, isErrorFetchingData }),
    [isErrorFetchingData, isLoadingData, tableData]
  )
}

export default useTeammateAvailabilityBuckets
